import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { budgetShare } from '@/lib/conversation-model';
import { callCostLabel, costLabel, exactCount, turnSeatLabel } from '@/lib/session-labels';
import { type SessionTurn, type TurnCall, turnTokens } from '@matter_extractor/shared';
import { TurnLayerShell } from './layer-shell';

/**
 * The Appels et coût layer of one turn (§8.7, §11.2, §12.3).
 *
 * ## The model RENDERED, and why the column exists at all
 *
 * « Le modèle rendu par l'API - jamais celui que Réglages a configuré » (§8.7).
 * A seat pointed at an alias, a router serving another revision, a provider
 * quietly retiring a dated id: all three are readable here and nowhere else. So
 * the column prints what the call answered, and a call that never got that far
 * says so rather than borrowing the setting's name.
 *
 * ## Four buckets and not one figure
 *
 * `turn.ts` states the reason and this table is what it was stated for: a cached
 * system prompt is written once and read afterwards at a tenth of the price, so
 * one flattened « tokens » figure would make a long conversation look
 * inexplicably cheap and an operator distrust the euro beside it.
 *
 * ## The total is the STORED one
 *
 * `turn.costEur` and never a sum of the rows: §8.7 stores it because the engine
 * accumulates it into `stepCostEur`, and « one figure with two sources is one
 * figure with two answers ». The rows explain it; they do not compute it.
 *
 * ## The stop reason appears only when it is not the ordinary one
 *
 * A turn marked « Tronqué » says the scribe was cut; this table is where an
 * operator sees WHICH call it was, in the provider's own word (issue #96). It
 * is printed beside the model and only when it is neither null nor `end_turn`,
 * the two answers a healthy row gives - a column that said `end_turn` on every
 * line would bury the one line worth reading.
 */

export interface LayerAppelsProps {
  turn: SessionTurn;
  /** The budget per step of §11.2, or null while Réglages has not answered. */
  budgetPerStepEur: number | null;
  open: boolean;
  onToggle: () => void;
}

/** `1,9 s` - written by hand, like every other figure of this lot (`Intl` follows the machine). */
function secondsLabel(ms: number): string {
  return `${(ms / 1000).toFixed(1).replace('.', ',')} s`;
}

export function LayerAppels({ turn, budgetPerStepEur, open, onToggle }: LayerAppelsProps) {
  const tokens = turnTokens(turn);
  const share = budgetShare(turn.snapshot.stepCostEur, budgetPerStepEur);
  const hint =
    turn.calls.length === 0
      ? `aucun appel enregistré · ${callCostLabel(turn.costEur)}`
      : `${turn.calls.length} appel${turn.calls.length > 1 ? 's' : ''} · ${callCostLabel(turn.costEur)} · ${exactCount(tokens)} jetons`;

  return (
    <TurnLayerShell layer="appels" hint={hint} open={open} onToggle={onToggle}>
      {turn.calls.length === 0 ? (
        <p className="text-gray-600">
          Ce tour n’a pas enregistré le détail de ses appels. Son coût, lui, est stocké :{' '}
          <b>{callCostLabel(turn.costEur)}</b>.
        </p>
      ) : (
        <CallsTable
          calls={turn.calls}
          tokens={tokens}
          costEur={turn.costEur}
          totalLabel="Total du tour"
        />
      )}

      <p className="flex flex-wrap items-center gap-2.5 text-gray-600 text-xs">
        <span>
          Coût cumulé de l’étape (essai {turn.snapshot.attempt}) :{' '}
          <b className="text-gray-900">{callCostLabel(turn.snapshot.stepCostEur)}</b>
          {budgetPerStepEur === null ? '' : ` sur un budget de ${costLabel(budgetPerStepEur)}`}
        </span>
        {share === null ? null : (
          <>
            {/*
              A native `progress` rather than a bar with a computed width: a
              `style` inline is what this package refuses for a fill (the
              segments of the involvement slider make the same argument), and
              the element already announces its own value.
            */}
            <progress
              value={share}
              max={100}
              aria-label="Part du budget de l’étape consommée"
              className="h-1.5 w-40 max-w-full accent-bleu-nuit"
            />
            <span className="tabular-nums">{share} %</span>
          </>
        )}
      </p>
    </TurnLayerShell>
  );
}

/**
 * The table of calls, one row per call and a total - the turn's, and the bubble's
 * of a page played in the conversation (issue #186), which has the one call.
 *
 * The total is the STORED cost the caller passes and never a sum of the rows (see
 * « The total is the STORED one » above), and the tokens are the caller's too.
 */
export function CallsTable({
  calls,
  tokens,
  costEur,
  totalLabel,
}: {
  calls: readonly TurnCall[];
  tokens: number;
  costEur: number;
  totalLabel: string;
}) {
  return (
    // The table is the one thing of this screen that cannot be made to fit a
    // phone: eight columns of figures. It scrolls inside its own frame
    // rather than making the page scroll sideways, the rule of the repo.
    <div className="-mx-1 overflow-x-auto px-1">
      <table className="w-full min-w-150 border-collapse text-sm">
        <thead>
          <tr>
            {['Siège', 'Modèle rendu'].map((head) => (
              <th
                key={head}
                scope="col"
                className="border-gray-200 border-b py-1.5 pr-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide"
              >
                {head}
              </th>
            ))}
            {['Entrée', 'Sortie', 'Cache écrit', 'Cache lu', 'Durée', 'Coût'].map((head) => (
              <th
                key={head}
                scope="col"
                className="border-gray-200 border-b py-1.5 pr-3 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calls.map((call, index) => (
            <tr
              // The seat is not unique - a retry adds a second row for the
              // same one, and that row is the whole reason the failures are
              // recorded. The index is what distinguishes them.
              key={`${call.seat}-${call.startedAt}-${index}`}
              data-testid={SESSIONS_TEST_IDS.turnCall}
              data-seat={call.seat}
            >
              <td className="border-gray-100 border-b py-1.5 pr-3 align-top">
                {turnSeatLabel(call.seat)}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 align-top">
                {call.model === null ? (
                  <span className="text-gray-500">aucune réponse</span>
                ) : (
                  <code className="break-all font-mono text-xs">{call.model}</code>
                )}
                {call.failedReason === null ? null : (
                  <span className="block break-words text-jaune-deep text-xs">
                    échec : {call.failedReason}
                  </span>
                )}
                {/*
                  The stop reason, and ONLY when it is neither null nor the
                  ordinary end of a turn (issue #96): printing `end_turn` on
                  every healthy row would be eight words of noise per turn,
                  where the whole value of the field is telling WHICH of the
                  two calls was cut. Null stays silent too - a call that
                  never returned already says « aucune réponse » above.
                */}
                {call.stopReason === null || call.stopReason === 'end_turn' ? null : (
                  <span className="block break-words text-jaune-deep text-xs">
                    arrêt : <code className="font-mono">{call.stopReason}</code>
                  </span>
                )}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 text-right align-top tabular-nums">
                {exactCount(call.inputTokens)}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 text-right align-top tabular-nums">
                {exactCount(call.outputTokens)}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 text-right align-top tabular-nums">
                {exactCount(call.cacheWriteTokens)}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 text-right align-top tabular-nums">
                {exactCount(call.cacheReadTokens)}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 text-right align-top tabular-nums">
                {secondsLabel(call.durationMs)}
              </td>
              <td className="border-gray-100 border-b py-1.5 pr-3 text-right align-top tabular-nums">
                {callCostLabel(call.costEur)}
              </td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td colSpan={2} className="py-1.5 pr-3">
              {totalLabel}
            </td>
            <td colSpan={4} className="py-1.5 pr-3 text-right tabular-nums">
              {exactCount(tokens)} jetons
            </td>
            <td className="py-1.5 pr-3 text-right tabular-nums">
              {secondsLabel(calls.reduce((total, call) => total + call.durationMs, 0))}
            </td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{callCostLabel(costEur)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
